import { LogEnum } from "../Enums/LogEnum";
import { ValidationMessageEnum } from "../Enums/ValidationMessageEnum";
import { ValidationResponseEnum } from "../Enums/ValidationResponseEnum";
import OddClientValidationModel from "../Models/OddClientValidationModel";
import SDKConfigurationModel from "../Models/SDKConfiguraitonModel";
import {
  OddClientConfig,
  OddClientCredentials,
} from "../Types/ConfiguratorTypes";
import IValidator from "./IValidator";

export default class OddClientValidator implements IValidator {
  private readonly keys = ["name", "credentials"];

  public validate = (
    config: SDKConfigurationModel
  ): OddClientValidationModel => {
    if (!config.oddClient) {
      const validation = new OddClientValidationModel();

      validation.validation = ValidationResponseEnum.MISSING_VALUE;
      validation.message = {
        type: LogEnum.WARNING,
        text: ValidationMessageEnum.ODD_CLIENT_MISSING,
      };

      return validation;
    }

    let validation = this.validateOddClient(config.oddClient);

    if (validation.message.type === LogEnum.ERROR) {
      return validation;
    }

    validation = this.validateOddClientConfigFields(
      config.oddClient,
      validation
    );
    validation = this.checkOddClientMissingProperties(validation);

    if (validation.message.type === LogEnum.ERROR) {
      return validation;
    }

    validation = this.validateOddClientCredentials(
      config.oddClient.credentials,
      validation
    );

    return validation;
  };

  private validateOddClient = (oddClient: OddClientConfig) => {
    const validationModel = new OddClientValidationModel();
    validationModel.message = { type: null, text: null };

    if (!(oddClient instanceof Object)) {
      validationModel.validation = ValidationResponseEnum.INVALID_VALUE;
      validationModel.message.type = LogEnum.ERROR;
      validationModel.message.text = ValidationMessageEnum.ODD_CLIENT_INVALID;

      return validationModel;
    }

    const oddClientConfigKeys = Object.keys(oddClient);
    const incorrectKeys = oddClientConfigKeys.filter(
      (key: string) => !this.keys.includes(key)
    );

    if (incorrectKeys.length > 0) {
      validationModel.validation = ValidationResponseEnum.INVALID_VALUE;
      validationModel.message.type = LogEnum.WARNING;
      validationModel.message.text = `${
        ValidationMessageEnum.ODD_CLIENT_CONFIG_INVALID_PROP
      } ${incorrectKeys.join(",")}`;
    } else {
      validationModel.validation = ValidationResponseEnum.VALID_VALUE;
    }

    return validationModel;
  };

  private validateOddClientConfigFields = (
    oddClient: OddClientConfig,
    validation: OddClientValidationModel
  ): any => {
    this.keys.forEach((key: string) => {
      if (oddClient[key]) {
        validation[key].validation = ValidationResponseEnum.VALID_VALUE;
      } else {
        validation[key].validation = ValidationResponseEnum.MISSING_VALUE;
      }
    });

    return validation;
  };

  private validateOddClientCredentials = (
    credentials: OddClientCredentials,
    validation: OddClientValidationModel
  ) => {
    if (!(credentials instanceof Object)) {
      validation.credentials.validation = ValidationResponseEnum.INVALID_VALUE;
      validation.credentials.message.type = LogEnum.ERROR;
      validation.credentials.message.text =
        ValidationMessageEnum.ODD_CLIENT_CREDENTIALS_INVALID;

      return validation;
    }

    const credentialsKeys = Object.keys(credentials);
    const correctKeys = ["username", "password"];
    const missingKeys = correctKeys.filter(
      (key: string) => !credentialsKeys.includes(key)
    );

    if (missingKeys.length > 0) {
      validation.credentials.validation = ValidationResponseEnum.INVALID_VALUE;
      validation.credentials.message.type = LogEnum.ERROR;
      validation.credentials.message.text = `${
        ValidationMessageEnum.ODD_CLIENT_CREDENTIALS_CONFIG_PROP_MISSING
      } ${missingKeys.join(", ")}`;
    }

    if (credentialsKeys.length > 0 && missingKeys.length === 0) {
      // We have provided keys. We need to check if they are correct

      if (!credentials.username || !credentials.password) {
        validation.credentials.validation =
          ValidationResponseEnum.INVALID_VALUE;
        validation.credentials.message.type = LogEnum.ERROR;
        validation.credentials.message.text =
          ValidationMessageEnum.ODD_CLIENT_CREDENTIALS_INVALID;
      }
    }

    const incorrectKeys = credentialsKeys.filter(
      (key: string) => !correctKeys.includes(key)
    );

    if (incorrectKeys.length > 0) {
      validation.credentials.validation = ValidationResponseEnum.INVALID_VALUE;
      validation.credentials.message.type = LogEnum.WARNING;
      validation.credentials.message.text = `${
        ValidationMessageEnum.ODD_CLIENT_CREDENTIALS_CONFIG_INVALID_PROP
      } ${incorrectKeys.join(", ")}`;
    }

    return validation;
  };

  private checkOddClientMissingProperties = (
    validation: OddClientValidationModel
  ) => {
    Object.keys(validation)
      .filter((key: string) => key !== "validation" && key !== "message")
      .forEach((key: string) => {
        const obj = validation[key];

        if (obj.validation === ValidationResponseEnum.MISSING_VALUE) {
          validation.validation = ValidationResponseEnum.INVALID_VALUE;
          validation.message.type = LogEnum.ERROR;

          validation.message.text =
            ValidationMessageEnum.ODD_CLIENT_CONFIG_MISSING_CONFIG;
        }
      });

    return validation;
  };
}
